import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Compass, Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import { lockedDialogProps } from './OnboardingLockCard'
import styles from './ProductTour.module.css'
import { SpotlightCutout, useSpotlightCard, useSpotlightTarget } from './Spotlight'
import spotlight from './Spotlight.module.css'
import coachGraceAvatarImg from '@/assets/brand/coach-grace-avatar.png'
import { Button } from '@/components/ui/button'
import { useTourAnchorNodes, useTourAnchors } from '@/contexts/TourAnchorContext'
import {
  isTourStopAvailable,
  TOUR_ANCHORS,
  TOUR_STOP_COUNT,
  TOUR_STOPS,
  tourStopAnchors,
  type TourContext,
} from '@/lib/productTour'

/**
 * The founder product tour — ClickUp 86d3uknyj / 86d3uknz4 (step 4).
 *
 * Seven stops, walked in order, skippable at any point; finishing and skipping
 * are the same server call, because "the founder has been shown around" is all
 * step 4 asserts and a skipped tour still satisfies it. That call completes step
 * 4, which auto-fires step 5 server-side — so the last thing this component does
 * is hand over to the unlock moment.
 *
 * It runs entirely inside the onboarding lock, on the founder home, and never
 * navigates: every surface it describes is a route the lock blocks. It points at
 * the chrome that leads there instead, which is why it is mounted from the
 * tracker inside `IconRailLayout` — the rail and the sidebar have to be on
 * screen behind it.
 *
 * The rest of the shell cannot be touched while it is up: Radix in modal mode
 * traps focus, marks everything outside the portal `aria-hidden` and locks
 * scroll. The spotlit elements are among what gets hidden, which is right rather
 * than unfortunate — they are dimmed, unclickable nav targets, and every card
 * names its target in words anyway.
 */
export function ProductTour({
  context,
  onFinish,
  finishing,
}: {
  context: TourContext
  /** Finish or skip. Both complete step 4; `skipped` only colours the metadata. */
  onFinish: (outcome: { skipped: boolean; stopsSeen: number }) => void
  /** The completion call is in flight — controls stay visible but inert. */
  finishing: boolean
}) {
  const [index, setIndex] = useState(0)
  // How far the founder actually got, which is not where they are standing: a
  // walk to stop 5, a Back to stop 3 and a skip has still shown them five stops,
  // and reporting three would understate the tour in the analytics it feeds.
  const [furthest, setFurthest] = useState(0)
  const goTo = (next: number) => {
    setIndex(next)
    setFurthest((seen) => Math.max(seen, next))
  }
  const stop = TOUR_STOPS[index]
  const isLast = index === TOUR_STOP_COUNT - 1

  const { register } = useTourAnchors()
  // Mounted candidates in fallback order; the spotlight picks the first that is
  // also on screen. Memoized because the identity is what the measuring
  // subscription re-runs on.
  const anchorIds = useMemo(() => tourStopAnchors(stop), [stop])
  const candidates = useTourAnchorNodes(anchorIds)
  const rect = useSpotlightTarget(candidates)
  const { cardRef, placement } = useSpotlightCard(rect)
  // Not simply "is there a rect": on a viewport too small for the card to sit
  // beside its target, the ring would be drawn under the card covering it.
  const framedRect = placement.framesTarget ? rect : null
  const available = isTourStopAvailable(stop, context)

  return (
    <>
      {/* Coach Grace's stand-in, registered like any other anchor. Outside the
          portal on purpose: it sits below the backdrop, so the dim falls on it
          like everything else in the shell and the cutout is what reveals it.
          Mounted for the whole tour rather than only on her stop, so it cannot
          pop into existence at the exact moment it is framed. */}
      <div ref={register(TOUR_ANCHORS.coachGraceProxy)} className={styles.coachGraceProxy} aria-hidden>
        <img src={coachGraceAvatarImg} alt="" className={styles.coachGraceProxyImage} />
      </div>

      <DialogPrimitive.Root open modal>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={spotlight.backdrop}
            data-dimmed={framedRect ? undefined : 'true'}
            data-testid="product-tour"
          >
            <SpotlightCutout rect={framedRect} />

            {/* A lock, not a dialog: the only ways out are the buttons below.
                `lockedDialogProps` is that guarantee, shared with the step
                overlay and the agreement so it cannot be weakened in one place
                and not the others. The card itself is this surface's own — a
                spotlight that moves with its target, not the standard card. */}
            <DialogPrimitive.Content
              ref={cardRef}
              className={`${spotlight.card} ${styles.card}`}
              // Absent when there is nothing to anchor to, which leaves the card
              // centred by its own stylesheet.
              style={placement.style}
              {...lockedDialogProps}
            >
              <div className={styles.header}>
                <Compass className={styles.headerIcon} aria-hidden />
                <span className={styles.progress} data-testid="product-tour-progress">
                  Stop {index + 1} of {TOUR_STOP_COUNT}
                </span>
              </div>

              <DialogPrimitive.Title asChild>
                <h2 className={styles.title}>{stop.title}</h2>
              </DialogPrimitive.Title>
              <DialogPrimitive.Description asChild>
                <p className={styles.body}>{stop.body}</p>
              </DialogPrimitive.Description>

              {/* An unavailable stop is shown, never skipped (decision #11). This
            note is the whole difference: where the feature lives and what it
            gives — with no claim about when the founder gets it, because the
            flags behind these surfaces are an org rollout decision that
            finishing onboarding does not change. */}
              {!available && (
                <p className={styles.unavailable} data-testid="product-tour-unavailable">
                  <Info className={styles.unavailableIcon} aria-hidden />
                  <span>
                    You will find this in {stop.location}. It is not available on your account yet —{' '}
                    {stop.whenAvailable}
                  </span>
                </p>
              )}

              {/* Decoration. Clickable dots would be a way to jump ahead, and the
            stops are meant to be walked in order. */}
              <div className={styles.dots} aria-hidden>
                {TOUR_STOPS.map((each, i) => (
                  <span key={each.id} className={styles.dot} data-state={dotState(i, index)} />
                ))}
              </div>

              <div className={styles.actions}>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="product-tour-skip"
                  disabled={finishing}
                  onClick={() => onFinish({ skipped: true, stopsSeen: furthest + 1 })}
                >
                  Skip the tour
                </Button>
                <div className={styles.actionsRight}>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="product-tour-back"
                    disabled={index === 0 || finishing}
                    onClick={() => goTo(Math.max(0, index - 1))}
                  >
                    Back
                  </Button>
                  {isLast ? (
                    <Button
                      size="sm"
                      data-testid="product-tour-finish"
                      disabled={finishing}
                      onClick={() => onFinish({ skipped: false, stopsSeen: TOUR_STOP_COUNT })}
                    >
                      Finish
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      data-testid="product-tour-next"
                      disabled={finishing}
                      onClick={() => goTo(Math.min(TOUR_STOP_COUNT - 1, index + 1))}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Overlay>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  )
}

function dotState(dotIndex: number, currentIndex: number): 'done' | 'current' | 'todo' {
  if (dotIndex < currentIndex) return 'done'
  return dotIndex === currentIndex ? 'current' : 'todo'
}
